import React, {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

//actions:
import {mediaItemPageActions} from "../../../redux/MediaItemPage/mediaItemRedux";

//components:
import {Carousel} from "../../../widgets";
import {CircularProgress, Fade} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import useTranslatedTitles from "./useTranslatedTitles";

const DetailsCarousels = ({selectedNode, filter1Selected, trackInfo = {}, selectedInsights}) => {
    const history = useHistory();
    const {t} = useTranslation();

    const filter1SelectedID = filter1Selected?.[0]?.id;
    const selectedNodeName = selectedNode?.name;

    //refs:
    const detailsRef = useRef(null);

    //REDUX:
    //store:
    const {
        infoVideo,
        videosFromSpeaker,
        videosCurrentSelection,
        videosCurrentTrack,
        videosInOtherTracks,
        isTextual,
    } = useSelector(state => state.trackPage.details);
    const trackVideos = useSelector(state => state.trackPage.trackVideos);
    const detailsError = useSelector(state => state.trackPage.detailsError);
    const isLoadingTrackInfo = useSelector(state => state.trackPage.isLoadingTrackInfo);
    const isLoadingExploreDetails = useSelector(state => state.trackPage.isLoadingExploreDetails);
    //actions:
    const dispatch = useDispatch();
    const setVideoTerm = term => dispatch(mediaItemPageActions.setVideoTerm(term));

    //---CAROUSELS TRANSLATED TITLES:
    const {
        allVideosTitle,
        selectedVideoCarouselTitle,
        speakersVideosTitle,
        videosAboutCurrentTrack,
        videosAboutOtherTracks,
        videosMentionedInCurrentSelectionTitle,
        videosAboutInCurrentSelectionTitle,
        videosMentionedInCurrentTrackTitle,
        videosAboutInCurrentTrackTitle,
        videosMentionedInOtherTracksTitle,
        videosAboutInOtherTracksTitle,
    } = useTranslatedTitles({
        selectedNode,
        trackInfo,
        videosFromSpeaker,
    });

    useEffect(() => {
        if (!isLoadingExploreDetails && selectedNode) {
            setTimeout(() => {
                detailsRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 400);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingExploreDetails]);

    const handleMatchClick = (id, time) => {
        if (selectedNodeName) {
            setVideoTerm({
                type: selectedNode.type,
                text: selectedNodeName,
                time,
                selectedNode,
            });
        }
        history.push(`/media/${id}`);
    };

    return (
        <div className="d-flex flex-column w-100 min-h-200px position-relative">
            {detailsError?.text ? (
                <div className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100">
                    <div className={`alert alert-${detailsError.type || "info"} font-size-h5 font-weight-bold`}>
                        <WarningRoundedIcon color="inherit" className="mr-5" />
                        {t(detailsError.text)}
                    </div>
                </div>
            ) : null}
            <div
                className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100"
                style={{
                    opacity: isLoadingExploreDetails ? 1 : 0,
                    zIndex: isLoadingExploreDetails ? 1 : -1,
                    transition: "0.4s ease all",
                }}
            >
                <CircularProgress />
            </div>

            {/* DETAILS SECTION: */}
            <Fade
                in={selectedNodeName}
                unmountOnExit={true}
                mountOnEnter={true}
                timeout={{
                    appear: 1500,
                    enter: 1500,
                    exit: 500,
                }}
            >
                <>
                    {/* //////////////////////////// ON TOPICS FILTER: */}

                    <div ref={detailsRef} />
                    {filter1SelectedID === "topics" && selectedNodeName ? (
                        <>
                            {videosCurrentTrack?.length ? (
                                <Carousel
                                    type="simple-video-card"
                                    htmlTagTitle={videosAboutCurrentTrack}
                                    titleClassName="mt-8"
                                    data={videosCurrentTrack}
                                    onclick={handleMatchClick}
                                />
                            ) : null}
                            {videosInOtherTracks?.length ? (
                                <Carousel
                                    type="simple-video-card"
                                    htmlTagTitle={videosAboutOtherTracks}
                                    titleClassName="mt-8"
                                    data={videosInOtherTracks}
                                    onclick={handleMatchClick}
                                />
                            ) : null}
                        </>
                    ) : null}

                    {/* //////////////////////////// ON VIDEOS/SPEAKERS FILTER: */}
                    {/* when clicked on MASTER node (Video/Speaker): */}
                    {((filter1SelectedID === "videos" || filter1SelectedID === "speakers") &&
                        selectedInsights?.[0]?.name) ||
                    filter1SelectedID === "highlights" ? (
                        <>
                            {filter1Selected[0].id === "videos" && infoVideo ? (
                                <Carousel
                                    type="simple-video-card"
                                    titleClassName="mt-8"
                                    htmlTagTitle={selectedVideoCarouselTitle}
                                    data={[infoVideo]}
                                    onclick={handleMatchClick}
                                />
                            ) : null}
                            {filter1Selected[0].id === "speakers" && videosFromSpeaker && videosFromSpeaker[0] ? (
                                <Carousel
                                    type="simple-video-card"
                                    titleClassName="mt-8"
                                    htmlTagTitle={speakersVideosTitle}
                                    data={videosFromSpeaker[0].videos}
                                    onclick={handleMatchClick}
                                />
                            ) : null}

                            {/* Selected SLAVE node: */}

                            {/* in current selection: */}
                            {videosCurrentSelection?.length && selectedNodeName ? (
                                isTextual ? (
                                    <Carousel
                                        type="video-card"
                                        titleClassName="mt-8"
                                        htmlTagTitle={videosMentionedInCurrentSelectionTitle}
                                        data={videosCurrentSelection}
                                        onclick={handleMatchClick}
                                        query={selectedNodeName}
                                    />
                                ) : (
                                    <Carousel
                                        type="simple-video-card"
                                        titleClassName="mt-8"
                                        htmlTagTitle={videosAboutInCurrentSelectionTitle}
                                        data={videosCurrentSelection}
                                        onclick={handleMatchClick}
                                    />
                                )
                            ) : null}

                            {/* in current track: */}
                            {videosCurrentTrack?.length && selectedNodeName ? (
                                isTextual ? (
                                    <Carousel
                                        type="video-card"
                                        titleClassName="mt-8"
                                        htmlTagTitle={videosMentionedInCurrentTrackTitle}
                                        data={videosCurrentTrack}
                                        onclick={handleMatchClick}
                                        query={selectedNodeName}
                                    />
                                ) : (
                                    <Carousel
                                        type="simple-video-card"
                                        titleClassName="mt-8"
                                        htmlTagTitle={videosAboutInCurrentTrackTitle}
                                        data={videosCurrentTrack}
                                        onclick={handleMatchClick}
                                    />
                                )
                            ) : null}

                            {/* in other tracks: */}
                            {videosInOtherTracks?.length && selectedNodeName ? (
                                isTextual ? (
                                    <Carousel
                                        type="video-card"
                                        htmlTagTitle={videosMentionedInOtherTracksTitle}
                                        data={videosInOtherTracks}
                                        onclick={handleMatchClick}
                                        query={selectedNodeName}
                                    />
                                ) : (
                                    <Carousel
                                        type="simple-video-card"
                                        htmlTagTitle={videosAboutInOtherTracksTitle}
                                        data={videosInOtherTracks}
                                        onclick={handleMatchClick}
                                    />
                                )
                            ) : null}
                        </>
                    ) : null}
                </>
            </Fade>

            {/* //////////////////// all track videos: */}
            <Fade
                in={!selectedNode || !!(selectedNode && !selectedNodeName)}
                mountOnEnter={true}
                unmountOnExit={true}
                timeout={{
                    appear: 1500,
                    enter: 1500,
                    exit: 500,
                }}
            >
                <div className={`d-flex flex-column position-relative`}>
                    {isLoadingTrackInfo ? (
                        <div className="d-flex justify-content-center align-items-center w-100 min-h-400px position-absolute">
                            <CircularProgress />
                        </div>
                    ) : trackVideos && trackVideos.length ? (
                        <Carousel
                            type="simple-video-card"
                            spaceBetween={20}
                            htmlTagTitle={allVideosTitle}
                            titleClassName="trackpage_all_videos_title"
                            data={trackVideos}
                            onclick={handleMatchClick}
                        />
                    ) : null}
                </div>
            </Fade>
        </div>
    );
};

export default DetailsCarousels;
